<script lang="ts">
	import { base } from '$app/paths';
	import * as api from '$lib/shared/apis';
	import { filteredTask } from '$lib/shared/filter';
	import { getLeaderboard } from '$lib/shared/queries';
	import { isAuthenticated } from '$lib/stores/auth';
	import { activeItem, loginOverlayIsVisible } from '$lib/stores/header';
	import { siteTitle } from '$lib/stores/nav';
	import { screen } from '$lib/stores/width';
	import UserTeaser from '$lib/UserTeaser.svelte';
	import VisibilityGuard from '$lib/VisibilityGuard.svelte';
	import { onMount } from 'svelte';

	let progress: Progress;

	export let data;

	let mounted = false;
	const imageCount = 10;

	type HighlightTask = 'TRANSCRIBE' | 'VERIFY';
	let highlightTask: HighlightTask = 'TRANSCRIBE';

	onMount(async () => {
		$siteTitle = 'Information';
		progress = await fetchProgress();
		mounted = true;
	});

	let processed: number;
	let progressProcessed: number;
	let progressApproved: number;

	$: if (progress) {
		processed = progress.total - progress.unprocessed;
		progressProcessed = processed / progress.total;
		progressApproved = progress.approved / progress.total;

		if (progressProcessed > 0.95) {
			highlightTask = 'VERIFY';
		}
	}

	const fetchProgress = async () => {
		const { response, json } = await api.post({
			query: /* GraphQL */ `query progress($projectName: ProjectName!){
				progress(project: $projectName) {
					unprocessed
                    incomplete
                    completed
                    approved
                    total
				}
			}`,
			variables: {
				projectName: import.meta.env.VITE_PROJECT_NAME,
			},
		});
		if (response.status === 200) {
			return json.data.progress;
		}
	};

	const daysDifference = (startDate, endDate) => {
		const oneDay = 24 * 60 * 60 * 1000;
		return Math.ceil((endDate.getTime() - startDate.getTime()) / oneDay);
	};

	let delta: number;
	if (data.project.expirationDate) {
		const endDate = new Date(data.project.expirationDate * 1000);
		const today = new Date();

		delta = daysDifference(today, endDate);
	}

	let heroSection;
	let currentIndex = 0;

	const onMousemoveHandler = (event: MouseEvent) => {
		const stripeWidth = Math.floor(heroSection.offsetWidth / imageCount);
		currentIndex = Math.min(Math.floor(event.offsetX / stripeWidth), imageCount - 1);
	};
</script>

<div class="md:h-[40rem] lg:h-screen relative mt-5 md:mt-[-4.25rem] flex flex-col justify-center">
	{#if mounted && $screen !== 'mobile'}
		<a
			href="#unter-der-zeitungsfalte"
			class="hidden md:flex justify-center items-center relative h-full"
			bind:this={heroSection}
			on:mousemove={onMousemoveHandler}
		>
			<picture>
				<img
					src="{base}/img/hero/{currentIndex + 1}.webp"
					alt=""
					class="absolute left-0 top-0 h-full w-full object-contain p-24 opacity-50"
				/>
			</picture>
		</a>
	{/if}
	<div class="flex flex-col items-center justify-center md:absolute inset-0 pointer-events-none">
		<h1 class="text-lg-fluid md:text-center max-w-7xl mx-auto">
			Tauchen Sie in die Welt der Jahre {data.project.fromYear}–{data.project.toYear} ein und unterstützen
			Sie uns bei der Transkription unserer Briefe
		</h1>

		<div
			class="hidden md:flex absolute bottom-0 left-0 w-full py-6 flex-wrap lg:flex-row justify-between whitespace-nowrap"
		>
			<div class="w-full lg:w-1/3">
				<a
					class="pointer-events-auto inline-flex items-center space-x-2 link-icon"
					href="#unter-der-zeitungsfalte"
					><span style="transform: rotate(90deg)">
						<svg width="18.707" height="14.707" aria-hidden="true" focusable="false"
							><use xlink:href="#icon-arrow" /></svg
						></span
					><span>Mehr über dieses Projekt</span></a
				>
			</div>
			{#if progress}
				<span class="sm:flex-1 lg:w-1/3 lg:text-center"
					>{highlightTask === 'TRANSCRIBE'
						? processed.toLocaleString()
						: progress.approved.toLocaleString()} von {progress.total.toLocaleString()}
					Briefen {highlightTask === 'TRANSCRIBE' ? 'transkribiert' : 'überprüft'}</span
				>
			{/if}
			<span class="sm:flex-1 lg:w-1/3 sm:text-right">
				{#if delta}
					Noch {delta} Tage
				{/if}</span
			>
		</div>
	</div>
</div>

<p class="md:hidden mt-5">Website in vollem Umfang auf Computer oder Tablet.</p>

<div class="md:hidden mt-10 mx-7 flex justify-center items-start">
	<picture>
		<img src="{base}/img/hero/{currentIndex + 1}.webp" alt="" width="280" height="420" />
	</picture>
</div>

<div id="unter-der-zeitungsfalte" class="mt-12 relative">
	{#if !$isAuthenticated}
		<div class="hidden md:block mb-[4.375rem] -mx-5 md:-mx-6 text-xl-fluid">
			<button
				class="relative whitespace-nowrap leading-tight w-full flex register-button overflow-hidden"
				on:click={() => {
					$activeItem = 'Registrieren';
					$loginOverlayIsVisible = true;
				}}
			>
				<span class="marquee" class:animate-pause={$loginOverlayIsVisible}>
					Registrieren Sie sich zum Mitmachen! <span aria-hidden="true">
						Registrieren Sie sich zum Mitmachen!
					</span>
				</span>
				<span
					class="marquee2 ml-4"
					class:animate-pause={$loginOverlayIsVisible}
					aria-hidden="true"
				>
					Registrieren Sie sich zum Mitmachen! Registrieren Sie sich zum Mitmachen!
				</span>
			</button>
		</div>
	{/if}

	<p class="text-lg-fluid">
		Durch Ihre Mithilfe werden im Rahmen dieser Crowdsourcing-Aktion unsere Briefe für alle
		lesbar.
	</p>

	<div class="flex flex-col md:flex-row mt-8 md:mt[4.375rem]">
		<div class="flex-1 flex flex-col">
			<a
				href="{base}/faq"
				class="hidden md:inline-flex items-center space-x-2 mt-auto link-icon"
				><span>Häufig gestellte Fragen</span><svg
					width="18.707"
					height="14.707"
					aria-hidden="true"
					focusable="false"><use xlink:href="#icon-arrow" /></svg
				></a
			>
		</div>
		<div class="flex-1 space-y-4">
			<p>
				Die Wienbibliothek im Rathaus bewahrt über 200.000 Briefe, Postkarten und andere
				Korrespondenzen aus mehreren Jahrhunderten. Innerhalb der nächsten Jahre holen wir
				diese aus unseren Depots und stellen sie der Öffentlichkeit digital und kostenfrei
				zur Verfügung. Aus den Jahren {data.project.fromYear}–{data.project.toYear} sind zurzeit
				{progress?.total.toLocaleString() || ''} digitalisierte Handschriften online, die einzigartige
				Einblicke in das Wiener Alltagsleben dieser von Krieg und Umbrüchen gezeichneten Jahre
				ermöglichen. Helfen Sie uns bei der inhaltlichen Erschließung dieser Unikate, indem Sie
				Texte transkribieren oder Beiträge anderer prüfen. Gemeinsam kann es uns gelingen, diese
				historisch wertvollen Dokumente für alle Interessierten nicht nur frei zugänglich, sondern
				auch les- und verstehbar zu machen. Die von Ihnen erstellten Transkriptionen werden nach
				Abschluss des Crowdsourcing-Projekts in unsere digitale Bibliothek integriert und sind
				dann jederzeit abrufbar und durchsuchbar.
			</p>
		</div>
	</div>
</div>

<div class="flex space-x-3 justify-center mt-52">
	<a
		class="button button-lg"
		href="{base}/briefe"
		on:click={() => {
			$filteredTask = 'TRANSCRIBE';
		}}>Transkribieren</a
	>
	<a
		class="button button-lg"
		href="{base}/briefe"
		on:click={() => {
			$filteredTask = 'VERIFY';
		}}>Prüfen</a
	>
	<a
		class="button button-lg"
		href="{base}/briefe"
		on:click={() => {
			$filteredTask = 'READ';
		}}>Lesen</a
	>
</div>

<p class="text-lg-fluid mt-52">
	Zurzeit können Sie auf die digitalisierten Briefe von Verfasser*innen mit Familiennamen
	{data.project.fromChar.toUpperCase()}&nbsp;bis&nbsp;{data.project.toChar.toUpperCase()} aus den Jahren
	{data.project.fromYear}–{data.project.toYear} zugreifen.
</p>
{#if progress}
	<div
		class="md:block mt-[6.25rem] mb-[4.375rem] flex flex-col w-max mx-auto"
		style="--progress: {(highlightTask === 'TRANSCRIBE'
			? progressProcessed
			: progressApproved) * 100}%"
	>
		<span class="progress">
			{highlightTask === 'TRANSCRIBE'
				? processed.toLocaleString()
				: progress.approved.toLocaleString()} von {progress.total.toLocaleString()}
			Briefen {highlightTask === 'TRANSCRIBE' ? 'transkribiert' : 'überprüft'}
		</span>

		<div
			class="progress-info"
			class:swap={processed / progress.total > 0.8}
			style="left: var(--progress)"
		>
			<ul class="uppercase text-sm-fluid">
				<li>{processed.toLocaleString()} transkribiert</li>
				<li>{progress.approved.toLocaleString()} überprüft</li>
				<li>{progress.total.toLocaleString()} Gesamt</li>
			</ul>
		</div>
	</div>
{/if}

<div>
	<h2 class="">Briefe</h2>
	<a href="{base}/briefe" class="mt-6 group relative flex -mx-6 px-6 overflow-hidden">
		<span class="flex space-x-2">
			{#each Array(imageCount) as _, index}
				<picture class="flex-shrink-0">
					<img
						src="{base}/img/hero/{index + 1}.webp"
						alt=""
						class="h-[400px] w-auto"
						loading="lazy"
					/>
				</picture>
			{/each}
		</span>
		<span
			class="absolute inset-y-0 -inset-x-5 md:-inset-x-6 flex items-center justify-center text-lg-fluid opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-50 group-focus-visible:opacity-100 link-icon"
			><span>Alle Briefe ansehen</span><svg
				width="30"
				height="24"
				viewBox="0 0 22.859 19"
				aria-hidden="true"
				focusable="false"
				class="ml-3"><use xlink:href="#icon-arrow" /></svg
			></span
		>
	</a>
</div>

<div class="hidden md:block mt-24">
	<VisibilityGuard let:hasBeenIsVisible height="300px">
		{#if hasBeenIsVisible}
			{#await getLeaderboard(3) then result}
				{#if result}
					<h2 class="text-lg-fluid">{result.total} Beiträger*innen</h2>

					<ul class="divide-y border-y mt-6">
						{#each result.users as user}
							<UserTeaser {user} />
						{/each}
					</ul>
				{/if}
			{/await}
		{/if}
	</VisibilityGuard>

	<a href="{base}/beitraeger-innen" class="inline-flex items-center space-x-2 link-icon mt-6"
		><span>Alle Beiträger*innen</span><svg
			width="18.707"
			height="14.707"
			aria-hidden="true"
			focusable="false"><use xlink:href="#icon-arrow" /></svg
		></a
	>
</div>

{#if !$isAuthenticated}
	<div class="text-center md:hidden">
		<button
			class="button mt-5"
			on:click={() => {
				$activeItem = 'Registrieren';
				$loginOverlayIsVisible = true;
			}}>Registrieren</button
		>
	</div>
{/if}

<style lang="postcss">
	.progress-info {
		position: relative;
		color: theme('colors.black');
		inline-size: max-content;
		margin-block-start: 2.625rem;
		padding-inline: 0.75rem;

		&::before {
			position: absolute;
			background-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="2" height="6"><circle cx="1" cy="2.5" r="1" fill="%23222" /></svg>');
			background-repeat: repeat-y;
			block-size: 100%;
			block-size: auto;
			content: '';
			inline-size: 0.125rem;
			inset-block-end: 0;
			inset-block-start: -9.75rem;
			inset-inline-start: -0.0625rem;
		}

		&.swap {
			text-align: end;
			transform: translate3d(-100%, 0, 0);

			&::before {
				inset-inline-end: -0.0625rem;
				inset-inline-start: auto;
			}

			& li {
				&::before {
					inset-inline-end: -0.75rem;
					inset-inline-start: auto;
					transform: translate3d(50%, 50%, 0);
				}
			}
		}

		& li {
			position: relative;

			&::before {
				position: absolute;
				display: block;
				background-color: #fff;
				block-size: 0.5rem;
				border-radius: 100%;
				box-shadow: inset 0 0 0px 1px currentColor;
				content: '';
				inline-size: 0.5rem;
				inset-inline-start: -0.75rem;
				transform: translate3d(-50%, 50%, 0);
			}
		}
	}

	.progress {
		background: linear-gradient(
			to right,
			black var(--progress),
			theme('colors.gray.45') var(--progress)
		);
		background-clip: text;
		color: transparent;
		cursor: default;
		font-size: min(5vw, 2.375rem);

		text-align: center;

		@media (min-width: 1024px) {
			font-size: 3.25rem;
		}
		@media (min-width: 1280px) {
			font-size: 4rem;
		}
		@media (min-width: 1440px) {
			font-size: 4.5rem;
		}
		@media (min-width: 1920px) {
			font-size: 6.25rem;
		}
	}

	.marquee {
		@media (prefers-reduced-motion: no-preference) {
			animation: marquee 20s linear infinite;
		}
	}

	.marquee2 {
		position: absolute;
		display: none;
		inset-block-start: 0;

		@media (prefers-reduced-motion: no-preference) {
			display: block;
			animation: marquee2 20s linear infinite;
		}
	}

	.register-button {
		&:hover,
		&:focus {
			& > * {
				animation-play-state: paused;
			}
		}
	}

	.animate-pause {
		animation-play-state: paused;
	}

	@keyframes marquee {
		from {
			transform: translate3d(0%, 0, 0);
		}
		to {
			transform: translate3d(-100%, 0, 0);
		}
	}

	@keyframes marquee2 {
		from {
			transform: translate3d(100%, 0, 0);
		}
		to {
			transform: translate3d(0%, 0, 0);
		}
	}
</style>
